import CryptoJS from "crypto-js";
import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { BASE_URL } from "../../../../api/baseURL";
import {
  processTravelCPR,
  transformVistaCPRData,
} from "../../../../api/cprServices";
import Input from "../../../../components/common/Input/Input";
import FileUploadInput from "../../../../components/common/newFileUploadInput/FileUploadInput";
import SelectInput from "../../../../components/common/selectInput/selectInput";
import SingleDatePicker from "../../../../components/common/SingleDatePicker/SingleDatePicker";
import FormTabs from "../../../../components/common/tabs/formTabs";
import TelInput from "../../../../components/common/telInput/telInput";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import PolicyCancellationMotorLayout from "../../../motorInsurancePages/policyOverview/managePolicy/policyCancellationFlow/policyCancellationMotorLayout";
import TravelBottomStickyBtnLayout from "../../../../components/newTravelPolicy/layouts/TravelBottomStickyBtnLayout";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import { LOCAL_STORAGE_NAME } from "../../../../redux/axiosInstance";
import {
  fetchNationalitiesList,
  selectNationalities,
  selectNationalitiesLoading,
} from "../../../../redux/features/constants/nationalitiesSlice";
import { selectLoginTokenInfo } from "../../../../redux/features/loginRegisterSlice";
import {
  daysOptions,
  monthsOptions,
  yearsOptions,
} from "../../../../utils/calenderOptions";

// Gender tabs
const genderTabs = [
  { label_en: "Male", label_ar: "ذكر", type: "Male" },
  { label_en: "Female", label_ar: "أنثى", type: "Female" },
];

// Relationship options for family members (labels set dynamically via translate)
const RELATIONSHIP_OPTIONS_DATA = [
  { code: "SPOUSE", value: "SPOUSE", label_en: "Spouse", label_ar: "زوج أو زوجة" },
  { code: "CHILD", value: "CHILD", label_en: "Child", label_ar: "ابن أو ابنة" },
];

const AddPassengerTravel = () => {
  const translate = useLanguageTranslate();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { policy_id } = useParams();

  // Build translated relationship options
  const relationshipOptions = RELATIONSHIP_OPTIONS_DATA.map((opt) => ({
    ...opt,
    label: translate(opt.label_en, opt.label_ar),
  }));

  // Decode policy number from URL
  const policyNumber = decodeURIComponent(policy_id);

  // Navigation function to policy overview
  const handleNavigationToPolicyOverview = () => {
    // We need to recreate the encrypted policy ID to navigate back
    const secretKey = "i-hate-meej";
    const encryptedValue = CryptoJS.AES.encrypt(
      policyNumber,
      secretKey
    ).toString();

    // Convert to URL-safe format (same as policy card)
    const urlSafeEncryptedValue = encryptedValue
      .split("+")
      .join("-") // Replace '+' with '-'
      .split("/")
      .join("_") // Replace '/' with '_'
      .split("=")
      .join(""); // Remove '=' padding

    // Navigate to policy overview with ManagePolicy tab
    navigate(
      `/travel-insurance/policy-overview/${urlSafeEncryptedValue}?tabType=ManagePolicy`
    );
  };

  // Nationality data from API
  const nationalityOptions = useSelector(selectNationalities);
  const nationalitiesLoading = useSelector(selectNationalitiesLoading);

  // Login token for API authentication
  const loginTokenInfo = useSelector(selectLoginTokenInfo);

  // Create default passenger object
  const createDefaultPassenger = () => ({
    id: 1,
    cprNumber: "",
    fullName: "",
    emailAddress: "",
    mobileNumber: "",
    nationality: "",
    gender: "",
    relationship: "",
    dateOfBirth: {
      day: "",
      month: "",
      year: "",
      fullDate: "",
    },
    cprUploaded: false,
    cprFiles: [],
    cprProcessing: false,
    cprData: null,
    cprProcessed: false,
  });

  // Form state - single passenger
  const [passenger, setPassenger] = useState(createDefaultPassenger);
  const [isLoading, setIsLoading] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [hasValidationAttempt, setHasValidationAttempt] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Mobile detection state
  const [isMobile, setIsMobile] = useState(false);

  // Mobile detection - Enhanced for DevTools testing
  useEffect(() => {
    const checkMobile = () => {
      const isMobileViewport = window.innerWidth <= 768;
      const isTouchDevice = "ontouchstart" in window;
      const isMobileUserAgent =
        /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
          navigator.userAgent
        );

      // Consider it mobile if viewport is small OR it's a mobile user agent
      // This helps with DevTools mobile simulation
      const isMobileDevice =
        isMobileViewport &&
        (isTouchDevice ||
          isMobileUserAgent ||
          window.navigator.maxTouchPoints > 0);
      setIsMobile(isMobileDevice);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch nationalities on component mount
  useEffect(() => {
    if (nationalityOptions.length === 0 && !nationalitiesLoading) {
      dispatch(fetchNationalitiesList());
    }
  }, [dispatch, nationalityOptions.length, nationalitiesLoading]);

  // Helper function to convert CPR nationality text to numeric code
  const convertNationalityToCode = (nationalityText) => {
    if (!nationalityText) return "";
    if (typeof nationalityText === "number") return nationalityText;
    if (nationalityOptions.length === 0) return nationalityText;

    const upperText = nationalityText.toUpperCase();
    const nationalityOption = nationalityOptions.find(
      (opt) =>
        opt.value?.toUpperCase() === upperText ||
        opt.label?.toUpperCase() === upperText ||
        opt.value?.toUpperCase().includes(upperText) ||
        upperText.includes(opt.value?.toUpperCase())
    );

    if (nationalityOption) {
      return nationalityOption.code;
    }
    return nationalityText;
  };

  // Helper function to get nationality code from nationality name
  const getNationalityCode = useCallback(
    (nationalityName) => {
      if (!nationalityName) return "14"; // Default to Bahrain

      const upperName = nationalityName.toUpperCase();
      const country = (nationalityOptions || []).find(
        (country) => country.value === upperName || country.label === upperName
      );

      return country ? country.code.toString() : "14";
    },
    [nationalityOptions]
  );

  // Custom validation function
  const validatePassengerFields = (passenger) => {
    const newErrors = {};

    // Relationship validation - required for add passenger
    if (!passenger.relationship?.trim()) {
      newErrors.relationship = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // CPR validation
    if (!passenger.cprNumber?.trim()) {
      newErrors.cprNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (passenger.cprNumber.length !== 9) {
      newErrors.cprNumber = translate(
        "CPR number must be 9 digits.",
        "يجب أن يتكون رقم الهوية الوطنية من 9 أرقام."
      );
    }

    // Full name validation
    if (!passenger.fullName?.trim()) {
      newErrors.fullName = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (!/^[a-zA-Z\s]+$/.test(passenger.fullName)) {
      newErrors.fullName = translate(
        "Name can only contain alphabetic characters, spaces, and periods.",
        "يجب أن يحتوي الاسم على حروف أبجدية ومسافات ونقاط فقط."
      );
    }

    // Email validation
    if (!passenger.emailAddress?.trim()) {
      newErrors.emailAddress = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (
      !/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(passenger.emailAddress)
    ) {
      newErrors.emailAddress = translate(
        "Invalid email address.",
        "البريد الإلكتروني غير صحيح."
      );
    }

    // Mobile validation
    if (!passenger.mobileNumber?.trim()) {
      newErrors.mobileNumber = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else if (passenger.mobileNumber.length !== 8) {
      newErrors.mobileNumber = translate(
        "Invalid mobile number.",
        "رقم الهاتف المحمول غير صحيح."
      );
    }

    // Nationality validation
    if (!passenger.nationality) {
      newErrors.nationality = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Gender validation
    if (!passenger.gender?.trim()) {
      newErrors.gender = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    }

    // Date of birth validation
    if (!passenger.dateOfBirth.fullDate?.trim()) {
      newErrors.dateOfBirth = translate(
        "This field is required.",
        "هذا الحقل مطلوب."
      );
    } else {
      const birthDate = new Date(passenger.dateOfBirth.fullDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (birthDate > today) {
        newErrors.dateOfBirth = translate(
          "Date of birth cannot be in the future.",
          "لا يمكن أن يكون تاريخ الميلاد في المستقبل."
        );
      } else if (passenger.relationship) {
        // Age validation based on relationship
        const age = Math.floor(
          (today - birthDate) / (365.25 * 24 * 60 * 60 * 1000)
        );

        if (passenger.relationship === "SPOUSE" && age < 18) {
          newErrors.dateOfBirth = translate(
            "Spouse must be 18 years or above.",
            "يجب أن يكون عمر الزوج/الزوجة 18 سنة أو أكثر."
          );
        } else if (passenger.relationship === "CHILD") {
          const ageInMonths = Math.floor(
            (today - birthDate) / (30.44 * 24 * 60 * 60 * 1000)
          );

          if (ageInMonths < 3) {
            newErrors.dateOfBirth = translate(
              "Child must be at least 3 months old.",
              "يجب أن يكون عمر الطفل 3 أشهر على الأقل."
            );
          } else if (age >= 18) {
            newErrors.dateOfBirth = translate(
              "Child must be under 18 years old.",
              "يجب أن يكون عمر الطفل أقل من 18 سنة."
            );
          }
        }
      }
    }

    return newErrors;
  };

  // Handle date change
  const handleDateChange = (field, value) => {
    setPassenger((prevPassenger) => {
      const currentDOB = prevPassenger.dateOfBirth || {
        day: "",
        month: "",
        year: "",
        fullDate: "",
      };

      const newDOB = {
        ...currentDOB,
        [field]: value,
      };

      if (newDOB.day && newDOB.month && newDOB.year) {
        newDOB.fullDate = `${newDOB.year}-${newDOB.month.padStart(
          2,
          "0"
        )}-${newDOB.day.padStart(2, "0")}`;
      } else {
        newDOB.fullDate = "";
      }

      return {
        ...prevPassenger,
        dateOfBirth: newDOB,
      };
    });

    // Clear date error when user selects any date field
    setTimeout(() => {
      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        dateOfBirth: "",
      }));
    }, 0);
  };

  // Handle input change
  const handleInputChange = (field, value) => {
    let processedValue = value;

    if (field === "cprNumber") {
      processedValue = value.replace(/\D/g, "");
      if (processedValue.length > 9) {
        processedValue = processedValue.substring(0, 9);
      }
    } else if (field === "fullName") {
      processedValue = value;
    }

    setPassenger((prevPassenger) => ({
      ...prevPassenger,
      [field]: processedValue,
    }));

    // Validate field after state update
    setTimeout(() => {
      const updatedPassenger = {
        ...passenger,
        [field]: processedValue,
      };

      const passengerErrors = validatePassengerFields(updatedPassenger);

      setFieldErrors((prevErrors) => ({
        ...prevErrors,
        [field]: passengerErrors[field] || "",
      }));
    }, 0);
  };

  // Handle file select
  const handleFileSelect = async (fileData) => {
    setTouchedFields((prev) => ({ ...prev, cpr: true }));

    setPassenger((prevPassenger) => ({
      ...prevPassenger,
      cprFiles: [...(prevPassenger.cprFiles || []), fileData],
      cprUploaded: true,
      cprProcessing: true,
    }));

    setFieldErrors((prevErrors) => ({ ...prevErrors, cpr: "" }));

    // Process CPR with Vista OCR
    if (fileData && fileData.file) {
      try {
        const result = await processTravelCPR(fileData.file);

        if (result.success && result.data?.output?.status) {
          const cprData = transformVistaCPRData(result.data);
          const nationalityCode = convertNationalityToCode(cprData.nationality);

          setPassenger((prevPassenger) => ({
            ...prevPassenger,
            cprNumber: cprData.cprNumber || prevPassenger.cprNumber,
            fullName: cprData.fullName || prevPassenger.fullName,
            dateOfBirth: cprData.dateOfBirth
              ? {
                  ...cprData.dateOfBirth,
                  fullDate:
                    cprData.dateOfBirth.day &&
                    cprData.dateOfBirth.month &&
                    cprData.dateOfBirth.year
                      ? `${
                          cprData.dateOfBirth.year
                        }-${cprData.dateOfBirth.month.padStart(
                          2,
                          "0"
                        )}-${cprData.dateOfBirth.day.padStart(2, "0")}`
                      : "",
                }
              : prevPassenger.dateOfBirth,
            gender: cprData.gender || prevPassenger.gender,
            nationality: nationalityCode || prevPassenger.nationality,
            cprProcessing: false,
            cprData: cprData,
            cprProcessed: true,
          }));

          toast.success(
            translate(
              "CPR information extracted successfully!",
              "تم استخراج معلومات الهوية بنجاح!"
            )
          );
        } else {
          setPassenger((prevPassenger) => ({
            ...prevPassenger,
            cprProcessing: false,
          }));

          toast.error(
            translate(
              "Failed to process CPR. Please fill the form manually.",
              "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
            )
          );
        }
      } catch (error) {
        setPassenger((prevPassenger) => ({
          ...prevPassenger,
          cprProcessing: false,
        }));

        toast.error(
          translate(
            "Failed to process CPR. Please fill the form manually.",
            "فشل في معالجة الهوية. يرجى ملء النموذج يدوياً."
          )
        );
      }
    }
  };

  // Handle file remove
  const handleFileRemove = () => {
    setTouchedFields((prev) => ({ ...prev, cpr: true }));

    setPassenger((prevPassenger) => ({
      ...prevPassenger,
      cprUploaded: false,
      cprFiles: [],
      cprProcessing: false,
      cprData: null,
      cprProcessed: false,
    }));
  };

  // Get field error
  const getFieldError = (field) => {
    return fieldErrors[field] || "";
  };

  // Function to check if CPR files are uploaded
  const getCprUploadError = () => {
    const fieldTouched = touchedFields.cpr;
    if (!fieldTouched && !hasValidationAttempt) {
      return "";
    }

    if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
      return translate("This field is required.", "هذا الحقل مطلوب.");
    }
    return "";
  };

  // Handle form submission
  const handleContinue = async () => {
    setHasValidationAttempt(true);

    let allValid = true;
    const newFieldErrors = {};

    // Validate passenger fields
    const passengerErrors = validatePassengerFields(passenger);
    Object.keys(passengerErrors).forEach((field) => {
      newFieldErrors[field] = passengerErrors[field];
      allValid = false;
    });

    // Check CPR upload requirement
    if (!passenger.cprFiles || passenger.cprFiles.length === 0) {
      allValid = false;
    }

    setFieldErrors(newFieldErrors);

    if (allValid) {
      setIsLoading(true);
      try {
        // Prepare API payload
        const payload = {
          PolicyNumber: policyNumber,
          FullName: passenger.fullName,
          DateOfBirth: passenger.dateOfBirth.fullDate,
          CPR: passenger.cprNumber,
          PhoneNumber: passenger.mobileNumber,
          Relationship: passenger.relationship,
          Gender: passenger.gender.charAt(0).toUpperCase(),
        };

        // Prepare headers with authentication (following same pattern as axiosInstance.js)
        const headers = {
          "Content-Type": "application/json",
        };

        // Get token from localStorage like axiosInstance does
        const token =
          JSON.parse(localStorage.getItem(LOCAL_STORAGE_NAME))?.access_token ||
          "";
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }

        // Call add member endpoint
        const response = await fetch(`${BASE_URL}/travel/add-member`, {
          method: "POST",
          headers: headers,
          body: JSON.stringify(payload),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          toast.success(
            translate(
              "Passenger added successfully!",
              "تم إضافة المسافر بنجاح!"
            )
          );
          // Navigate to manage policy screen
          navigate(
            `/travel-insurance/policy-overview/${policy_id}?tabType=ManagePolicy`
          );
        } else {
          toast.error(
            result.message ||
              translate("Failed to add passenger", "فشل في إضافة المسافر")
          );
        }
      } catch (error) {
        toast.error(
          translate(
            "An error occurred while adding the passenger",
            "حدث خطأ أثناء إضافة المسافر"
          )
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  // Render passenger form (modified from contactDetails.jsx)
  const renderPassengerForm = () => {
    return (
      <div className="mb-6 pb-10 lg:pb-28">

        <div className="mb-6 mt-2">
          <p className="text-[#252C32] text-base font-semibold mb-2">
            {translate(
              "Passenger Details",
              "تفاصيل المسافر"
            )}
          </p>
          <hr className="bg-[#E5E9EB]" />
        </div>

        {/* CPR Upload */}
        <div className="mb-6">
          <label className="mb-1 text-[#48535B] block text-base">
            {translate(
              "Upload CPR (front & back)",
              "تحميل الهوية الوطنية (من الأمام والخلف)"
            )}
          </label>
          <FileUploadInput
            name="cpr_passenger_1"
            onFileSelect={handleFileSelect}
            onFileRemove={handleFileRemove}
            onLoseFocus={() =>
              setTouchedFields((prev) => ({ ...prev, cpr: true }))
            }
            validTypes={["image/jpeg", "image/png", "application/pdf"]}
            maxSizeKB={5000}
            uploadToServer={true}
            multiple={true}
            maxFiles={2}
            disabled={passenger.cprProcessing}
            initialFiles={passenger.cprFiles || []}
          />

          {/* CPR Processing Status */}
          {passenger.cprProcessing && (
            <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center space-x-2 rtl:space-x-reverse">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                <p className="text-sm text-blue-700">
                  {translate(
                    "Processing CPR data...",
                    "جاري معالجة بيانات الهوية..."
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Show inline error for CPR upload */}
          {getCprUploadError() && (
            <div className="mt-2 text-red-600 text-sm">
              {getCprUploadError()}
            </div>
          )}
        </div>

        {/* CPR Number */}
        <div className="mb-3">
          <Input
            id="cprNumber_1"
            label={translate("CPR Number", "رقم الهوية الوطنية")}
            type="text"
            value={passenger.cprNumber}
            handleChange={(e) => handleInputChange("cprNumber", e.target.value)}
            error={getFieldError("cprNumber")}
            maxLength={9}
            disabled={passenger.cprProcessing}
          />
        </div>

        {/* Full Name */}
        <div className="mb-3">
          <Input
            id="fullName_1"
            label={translate("Full Name", "الاسم الكامل")}
            type="text"
            value={passenger.fullName}
            handleChange={(e) => handleInputChange("fullName", e.target.value)}
            error={getFieldError("fullName")}
            disabled={passenger.cprProcessing}
          />
        </div>

        {/* Email Address */}
        <div className="mb-3">
          <Input
            id="emailAddress_1"
            label={translate("Email Address", "عنوان البريد الإلكتروني")}
            type="email"
            value={passenger.emailAddress}
            handleChange={(e) =>
              handleInputChange("emailAddress", e.target.value)
            }
            error={getFieldError("emailAddress")}
          />
        </div>

        {/* Mobile Number */}
        <div className="mb-3">
          <TelInput
            name="mobileNumber"
            value={passenger.mobileNumber}
            error={getFieldError("mobileNumber")}
            onChange={(e) => handleInputChange(e.target.name, e.target.value)}
            label={translate("Mobile Number", "رقم الهاتف المحمول")}
          />
        </div>

        {/* Nationality */}
        <div
          className={`mb-3 ${
            passenger.cprProcessing ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <SelectInput
            name="nationality"
            formData={passenger}
            label={translate("Nationality", "الجنسية")}
            options_data={nationalityOptions || []}
            error={getFieldError("nationality")}
            handleChange={(e) =>
              !passenger.cprProcessing &&
              handleInputChange(e.target.name, e.target.value)
            }
            default_placeholder={translate(
              "Please select an option.",
              "يرجى اختيار خيار."
            )}
            disabled={passenger.cprProcessing || nationalitiesLoading}
          />
        </div>

        {/* Gender */}
        <div
          className={`mb-3 ${
            passenger.cprProcessing ? "opacity-50 pointer-events-none" : ""
          }`}
        >
          <FormTabs
            label={translate("Gender", "الجنس")}
            tabs={genderTabs}
            onTabClick={(type) =>
              !passenger.cprProcessing && handleInputChange("gender", type)
            }
            activeTab={passenger.gender}
            error={getFieldError("gender")}
          />
        </div>

        {/* Date of Birth - with mobile native date picker */}
        <div className="date_of_birth mb-3">
          <label className="block mb-[5px] text-[#48535B] not-italic font-normal leading-6 text-base max-350:text-[13px]">
            {translate("Date of Birth", "تاريخ الميلاد")}
          </label>

          {isMobile ? (
            // Mobile: Use SingleDatePicker with native date picker
            <SingleDatePicker
              value={passenger.dateOfBirth?.fullDate || ""}
              onChange={(dateString) => {
                if (!dateString) return;
                // Parse the date string (YYYY-MM-DD format from mobile date picker)
                const [year, month, day] = dateString.split("-");

                setPassenger((prevPassenger) => ({
                  ...prevPassenger,
                  dateOfBirth: {
                    day: day,
                    month: month,
                    year: year,
                    fullDate: dateString,
                  },
                }));

                // Clear date error
                setTimeout(() => {
                  setFieldErrors((prevErrors) => ({
                    ...prevErrors,
                    dateOfBirth: "",
                  }));
                }, 0);
              }}
              placeholder={translate("Select Date", "اختر التاريخ")}
              error={getFieldError("dateOfBirth")}
              useNativeMobile={true}
              disabled={passenger.cprProcessing}
            />
          ) : (
            // Desktop: Keep existing select dropdowns
            <div className="flex gap-6 self-stretch">
              <SelectInput
                name="day"
                options_data={daysOptions}
                formData={{ day: passenger.dateOfBirth?.day || "" }}
                default_placeholder={translate("Day", "يوم")}
                error=""
                handleChange={(e) => handleDateChange("day", e.target.value)}
                disabled={passenger.cprProcessing}
                maxMenuHeight={185}
              />

              <SelectInput
                name="month"
                options_data={monthsOptions}
                formData={{ month: passenger.dateOfBirth?.month || "" }}
                default_placeholder={translate("Month", "شهر")}
                error=""
                handleChange={(e) => handleDateChange("month", e.target.value)}
                disabled={passenger.cprProcessing}
                maxMenuHeight={185}
              />

              <SelectInput
                name="year"
                options_data={yearsOptions}
                formData={{ year: passenger.dateOfBirth?.year || "" }}
                default_placeholder={translate("Year", "سنة")}
                error=""
                handleChange={(e) => handleDateChange("year", e.target.value)}
                disabled={passenger.cprProcessing}
                maxMenuHeight={185}
              />
            </div>
          )}

          {/* Single error message for desktop date fields only (mobile SingleDatePicker shows its own error) */}
          {!isMobile && getFieldError("dateOfBirth") && (
            <p className="text-red-500 text-sm mt-1">
              {getFieldError("dateOfBirth")}
            </p>
          )}
        </div>

        {/* Relationship */}
        <div className="mb-3">
          <SelectInput
            name="relationship"
            formData={passenger}
            label={translate("Relationship", "العلاقة")}
            options_data={relationshipOptions}
            error={getFieldError("relationship")}
            handleChange={(e) =>
              handleInputChange(e.target.name, e.target.value)
            }
            default_placeholder={translate(
              "Please select relationship",
              "يرجى اختيار العلاقة"
            )}
          />
        </div>
      </div>
    );
  };

  return (
    <PolicyCancellationMotorLayout
      is_hide_back_btn={false}
      show_close_btn={false}
      isFirstStep={false}
      backClickHandler={handleNavigationToPolicyOverview}
      handleCloseBtnClick={handleNavigationToPolicyOverview}
      is_bottom_sticky_layout={false}
    >
      <TravelBottomStickyBtnLayout
        btn_onClick={handleContinue}
        btn_disabled={isLoading}
        btn_label={translate("Confirm", "تأكيد")}
      >
        <div className="w-[32%] max767:w-full mx-auto ipad:min-w-[66%] pb-5 max767:px-4 max767:pb-20 pt-[1%]">
          <TitleDescription
            title={translate("Add Passenger", "إضافة مسافر")}
            description={translate(
              "Please provide the necessary information below.",
              "الرجاء ملء الخانات."
            )}
          />

          {renderPassengerForm()}
        </div>
      </TravelBottomStickyBtnLayout>
    </PolicyCancellationMotorLayout>
  );
};

export default AddPassengerTravel;
